import React, { JSX, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SpaceList from "./views/admin/space/SpaceList";
import Login from "./views/auth/login/Login";
import "./index.css";
import Register from "./views/auth/register/Register";
import SubscriptionPlanList from "./views/admin/subscriptionPlan/SubscriptionPlanList";
import ProfilePage from "./views/main/profile/Profile";
// Admin
import AdminFolderRepository from './views/admin/folder/FolderRepository';
import AdminFolderDetail from "./views/admin/folder/FolderDetail";
// Super Admin
import CompanyList from "./views/superadmin/company/CompanyList";
import SuperAdminFolderRepository from './views/superadmin/folder/FolderRepository';
import SuperAdminFolderDetail from './views/superadmin/folder/FolderDetail';
import SuperAdminUserList from "./views/admin/user/UserList";
import SuperAdminAssignAccess from "./views/admin/user/AssignAccess";

// User
import FolderRepository from './views/main/folder/FolderRepository';
import FolderDetail from "./views/main/folder/FolderDetail";
// Company Admin
import CompanyAdminUserList from "./views/companyadmin/user/UserList";
import CompanyAdminAssignAccess from "./views/companyadmin/user/AssignAccess";
import CompanyAdminFolderRepository from './views/companyadmin/folder/FolderRepository';
import CompanyAdminFolderDetail from './views/companyadmin/folder/FolderDetail';
import FileViewerPage from "./components/folderRepo/FileViwerPage";

const AUTO_LOGOUT_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

console.log(process.env.NODE_ENV);

// Higher-Order Component to handle private routes
const ProtectedRoute: React.FC<{ element: JSX.Element }> = ({ element }) => {
  const token = localStorage.getItem("token");
  return token ? element : <Navigate to="/login" replace />;
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(AUTO_LOGOUT_TIME / 1000); // Convert ms to seconds

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = userData?.role;

  const getDefaultRoute = () => {
    if (userRole === 'superadmin') {
      return "/sa/users";
    }

    if (userRole === 'admin') {
      return "/spaces";
    }

    // Check if user is company admin
    if (userRole === 'user' && userData?.is_company_admin === "1") {
      return "/ca/users";
    }

    // Default for normal user
    return "/folderRepo";
  };


  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsAuthenticated(!!token); // Check authentication status

    let logoutTimer: NodeJS.Timeout;
    let countdownInterval: NodeJS.Timeout;

    const resetLogoutTimer = () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      if (countdownInterval) clearInterval(countdownInterval);

      setCountdown(AUTO_LOGOUT_TIME / 1000); // Reset countdown to 5 minutes

      logoutTimer = setTimeout(() => {
        handleLogout();
      }, AUTO_LOGOUT_TIME);

      countdownInterval = setInterval(() => {
        setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    };

    const handleLogout = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      toast.info("You have been logged out due to inactivity.", { position: "top-right" });
      window.location.reload();
    };

    // Event listeners to detect user activity
    const events = ["mousemove", "keydown", "click"];
    events.forEach((event) => window.addEventListener(event, resetLogoutTimer));

    // Start the inactivity timer and countdown
    resetLogoutTimer();

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetLogoutTimer));
      if (logoutTimer) clearTimeout(logoutTimer);
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, []);

  return (
    <>
      <ToastContainer />
      <Router>
        {isAuthenticated} {/* Pass countdown to Topbar */}

        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          <Route path="*" element={<Navigate to={isAuthenticated ? getDefaultRoute() : "/login"} replace />} />

          {/* Admin */}
          <Route path="/admin/folderRepo/:spaceId" element={<ProtectedRoute element={<AdminFolderRepository />} />} />
          <Route path="/admin/folder/:id/:spaceId" element={<ProtectedRoute element={<AdminFolderDetail />} />} />

          {/* Superadmin */}
          <Route path="/sa/users" element={<ProtectedRoute element={<SuperAdminUserList />} />} />
          <Route path="/sa/assign-access/:userId/:companyId" element={<SuperAdminAssignAccess />} />
          <Route path="/sa/companies" element={<ProtectedRoute element={<CompanyList />} />} />
          <Route path="/sa/folderRepo/:spaceId" element={<ProtectedRoute element={<SuperAdminFolderRepository />} />} />
          <Route path="/sa/folder/:id/:spaceId" element={<ProtectedRoute element={<SuperAdminFolderDetail />} />} />

          {/* Company Admin */}
          <Route path="/ca/users" element={<ProtectedRoute element={<CompanyAdminUserList />} />} />
          <Route path="/ca/assign-access/:userId/:companyId" element={<CompanyAdminAssignAccess />} />
          <Route path="/ca/folderRepo" element={<ProtectedRoute element={<CompanyAdminFolderRepository />} />} />
          <Route path="/ca/folder/:id" element={<ProtectedRoute element={<CompanyAdminFolderDetail />} />} />

          {/* Private Routes */}
          <Route path="/folderRepo" element={<ProtectedRoute element={<FolderRepository />} />} />
          <Route path="/folder/:id" element={<ProtectedRoute element={<FolderDetail />} />} />

          <Route
            path="/file-viewer/:fileUrlEncoded/:fileName"
            element={<FileViewerPage />}
          />


          <Route path="/spaces" element={<ProtectedRoute element={<SpaceList />} />} />
          <Route path="/plans" element={<ProtectedRoute element={<SubscriptionPlanList />} />} />
          <Route path="/profile" element={<ProtectedRoute element={<ProfilePage />} />} />
        </Routes>
      </Router>
    </>
  );
};

export default App;
